import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Camera, Check, Menu, X } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { captureUtm, trackEvent, metaEventId, metaTrack, metaMirror, signupHref } from "@/lib/analytics";
import { LogoIcon } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { changeLang } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";

// Homepage v2 — spec-driven rebuild. Solid navy design tokens, Manrope only,
// no gradients, no stock art: mockups are HTML recreations of real app screens
// using the authorized demo project's real jobsite photos (story-*.webp).
// Exactly one <h1>. All CTAs carry utm_*/fbclid through to /signup.

const NAVY = "#0B1A2E";
const CHIP_NAVY = "#13294A";
const FOOTER_BG = "#08131F";
const BLUE = "#1565D8";
const LIGHT = "#F4F7FB";

const wrap = "mx-auto w-full max-w-[1160px] px-5";
const label = "text-[13px] font-extrabold uppercase tracking-[1.2px]";
const h2cls = "text-[clamp(28px,3.4vw,40px)] font-extrabold tracking-tight text-[#0F172A]";
const ctaMain = "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#1565D8] px-[28px] py-[17px] text-[18px] font-extrabold text-white transition-colors hover:bg-[#0F4FA8] active:scale-95";

// Real jobsite photos from the authorized demo project — never stock art.
const PHONE_PHOTOS = [
  { src: "/landing/v2/story-1.webp", time: "7:42 AM" },
  { src: "/landing/v2/story-2.webp", time: "8:15 AM" },
  { src: "/landing/v2/story-3.webp", time: "9:03 AM" },
  { src: "/landing/v2/story-4.webp", time: "9:40 AM" },
  { src: "/landing/v2/story-5.webp", time: "10:12 AM" },
  { src: "/landing/v2/story-6.webp", time: "11:05 AM" },
];

const COST_ROWS = [
  ["v2_jc_row_materials", "$2,140.00"],
  ["v2_jc_row_subs", "$1,500.00"],
  ["v2_jc_row_rental", "$420.50"],
  ["v2_jc_row_disposal", "$220.00"],
];

function SectionLabel({ color, children, testid }) {
  return <p className={label} style={{ color }} data-testid={testid}>{children}</p>;
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [billInt, setBillInt] = useState("year");
  const [founding, setFounding] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/founding-spots`)
      .then((r) => r.json()).then(setFounding).catch(() => {});
  }, []);
  const isEs = i18n.language?.startsWith("es");
  useSeo({ title: t("seo_home_title"), description: t("seo_home_desc") });
  const toSignup = () => navigate(signupHref());
  const loginLabel = user ? t("action_open_app", "Open App") : t("v2_nav_login");

  useEffect(() => {
    captureUtm();
    trackEvent("landing_view");
    const el = document.getElementById("pricing");
    if (el && "IntersectionObserver" in window) {
      const ob = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          trackEvent("pricing_viewed");
          const id = metaEventId();
          metaTrack("ViewContent", id);   // Meta pixel + CAPI mirror, same event_id
          metaMirror("ViewContent", id);
          ob.disconnect();
        }
      }, { threshold: 0.35 });
      ob.observe(el);
      return () => ob.disconnect();
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#334155]" style={{ fontFamily: "Manrope, system-ui, sans-serif" }} data-testid="landing">

      {/* 1. Header — navy, sticky. Phones: logo + Start free + menu only. */}
      <header className="sticky top-0 z-40" style={{ background: NAVY }} data-testid="landing-nav">
        <div className={`${wrap} flex items-center justify-between gap-3 py-3`}>
          <Link to="/" aria-label="BuildProject360 home" data-testid="logo-home-link" className="flex items-center gap-2.5">
            <LogoIcon size={36} />
            <span className="text-[17px] font-extrabold tracking-tight text-white max-[400px]:hidden" data-testid="logo-wordmark">
              BuildProject<span style={{ color: "#3B9BFF" }}>360</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-7 md:flex" data-testid="nav-links">
            <a href="#features" data-testid="nav-features" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-[#C7D3E3] transition-colors hover:text-white">{t("v2_nav_features")}</a>
            <a href="#pricing" data-testid="nav-pricing" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-[#C7D3E3] transition-colors hover:text-white">{t("v2_nav_pricing")}</a>
            <button onClick={() => navigate(user ? "/app" : "/login")} data-testid="nav-login"
              className="inline-flex min-h-[44px] items-center text-sm font-semibold text-[#C7D3E3] transition-colors hover:text-white">
              {loginLabel}
            </button>
            <button onClick={toSignup} data-testid="cta-start-free-nav"
              className="inline-flex min-h-[44px] items-center rounded-full px-5 text-sm font-extrabold text-white transition-colors hover:bg-[#0F4FA8] active:scale-95" style={{ background: BLUE }}>
              {t("v2_cta")}
            </button>
          </nav>
          <div className="flex items-center gap-2 md:hidden">
            <button onClick={() => navigate(user ? "/app" : "/login")} data-testid="nav-login-mobile"
              className="inline-flex min-h-[44px] items-center rounded-full border border-white/40 px-3.5 text-sm font-bold text-white active:scale-95">
              {loginLabel}
            </button>
            <button onClick={toSignup} data-testid="cta-start-free-nav-mobile"
              className="inline-flex min-h-[44px] items-center rounded-full px-4 text-sm font-extrabold text-white active:scale-95" style={{ background: BLUE }}>
              {t("v2_cta_short")}
            </button>
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="nav-menu-toggle" aria-label={t("v2_menu")} aria-expanded={menuOpen}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white active:scale-95">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-white/10 px-5 pb-4 pt-3 md:hidden" data-testid="nav-menu">
            <button onClick={() => { setMenuOpen(false); navigate(user ? "/app" : "/login"); }} data-testid="nav-login-m"
              className="mb-2 flex min-h-[48px] w-full items-center justify-center rounded-xl border border-white/30 text-sm font-extrabold text-white active:scale-95">{loginLabel}</button>
            {[
              ["nav-features-m", t("v2_nav_features"), "#features"],
              ["nav-pricing-m", t("v2_nav_pricing"), "#pricing"],
            ].map(([tid, lbl, href]) => (
              <a key={tid} href={href} onClick={() => setMenuOpen(false)} data-testid={tid}
                className="flex min-h-[44px] items-center text-sm font-semibold text-[#C7D3E3]">{lbl}</a>
            ))}
            <div className="flex min-h-[44px] items-center"><LanguageToggle /></div>
          </div>
        )}
      </header>

      {/* 2. Hero — solid navy, text never on a photo. Phone mockup below on phones. */}
      <section style={{ background: NAVY }} data-testid="landing-hero">
        <div className={`${wrap} flex flex-wrap items-center gap-6 py-6 md:gap-10 md:py-20`}>
          <div className="min-w-0 flex-[1_1_420px]">
            <span className="inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-extrabold uppercase tracking-[1.2px] text-[#8FC3FF]"
              style={{ background: CHIP_NAVY }} data-testid="hero-pill">{t("v2_hero_pill")}</span>
            <h1 className="mt-4 text-[clamp(36px,5vw,58px)] font-extrabold leading-[1.05] tracking-[-1.2px] text-white" data-testid="hero-headline">
              {t("v2_hero_h1")}
            </h1>
            <p className="mt-3 max-w-xl text-[17px] leading-relaxed text-[#C7D3E3] md:text-[19px]" data-testid="hero-sub">{t("v2_hero_sub")}</p>
            <button onClick={toSignup} data-testid="cta-start-free-hero" className={`mt-6 md:mt-7 ${ctaMain}`}>
              {t("v2_cta")} <ArrowRight className="h-5 w-5" />
            </button>
            <p className="mt-3 text-sm font-semibold text-[#A9B8CC]" data-testid="hero-underline">{t("v2_hero_line")}</p>
          </div>
          <div className="flex flex-[1_1_420px] justify-center">
            <div className="w-[262px] overflow-hidden rounded-[40px] border-4 border-[#1E2F47] bg-white shadow-2xl sm:w-[290px] sm:border-[6px]" data-testid="hero-phone">
              <div className="px-4 py-2 sm:py-3" style={{ background: NAVY }}>
                <p className="truncate text-[13px] font-extrabold text-white" data-testid="hero-phone-project">{t("v2_mock_project")}</p>
                <p className="truncate text-[10px] font-semibold text-[#A9B8CC]" data-testid="hero-phone-addr">{t("v2_mock_addr")}</p>
                <p className="text-[11px] font-semibold text-[#A9B8CC]" data-testid="hero-phone-count">
                  <span className="max-sm:hidden">{t("v2_mock_today")}</span><span className="sm:hidden">{t("v2_mock_today_m")}</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-1.5 bg-white p-2">
                {PHONE_PHOTOS.map((p, i) => (
                  <div key={p.src} className={`relative aspect-[4/3] overflow-hidden rounded-lg bg-[#EDF2F8] ${i > 1 ? "max-sm:hidden" : ""}`}>
                    <img src={p.src} alt={t("v2_mock_photo_alt")} width={500} height={335} loading={i < 2 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : undefined} decoding="async" className="h-full w-full object-cover" />
                    <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[8px] font-bold text-white">{p.time} · GPS</span>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 text-center text-[11px] font-bold text-[#0E6B40] sm:py-2.5" style={{ background: "#EAF6EF" }} data-testid="hero-phone-saved">
                {t("v2_mock_saved")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Trust strip */}
      <section style={{ background: LIGHT }} data-testid="landing-trust">
        <p className={`${wrap} py-2.5 text-center text-sm font-semibold text-[#475569]`} data-testid="trust-line">{t("v2_trust")}</p>
      </section>

      {/* 4. Jobsite photos + Share with customer — #features */}
      <section id="features" className="bg-white" data-testid="landing-photos">
        <div className={`${wrap} flex flex-wrap items-center gap-6 py-6 md:gap-10 md:py-20`}>
          <div className="min-w-0 flex-[1_1_420px]">
            <SectionLabel color="#1259C3" testid="photos-label">{t("v2_ph_label")}</SectionLabel>
            <h2 className={`mt-3 ${h2cls}`} data-testid="photos-h2">{t("v2_ph_h2")}</h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#334155] md:text-base" data-testid="photos-body">{t("v2_ph_body")}</p>
            <ul className="mt-4 space-y-2.5 md:mt-6 md:space-y-3">
              {["v2_ph_b1", "v2_ph_b2", "v2_ph_b3"].map((k) => (
                <li key={k} className="flex items-center gap-2.5" data-testid={`photos-check-${k}`}>
                  <Check className="h-5 w-5 shrink-0" style={{ color: BLUE }} />
                  <span className="text-[15px] font-semibold text-[#0F172A]">{t(k)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-[1_1_420px]">
            <div className="rounded-[20px] border border-[#E4EAF2] p-5 md:p-6" style={{ background: LIGHT }} data-testid="share-card">
              <SectionLabel color="#1259C3" testid="share-label">{t("v2_sh_label")}</SectionLabel>
              <p className="mt-3 text-xl font-extrabold leading-snug text-[#0F172A]" data-testid="share-big">{t("v2_sh_big")}</p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {["/landing/v2/story-1.webp", "/landing/v2/story-3.webp", "/landing/v2/story-5.webp"].map((src, i) => (
                  <figure key={["v2_sh_before", "v2_sh_framing", "v2_sh_after"][i]}>
                    <div className="aspect-[4/3] overflow-hidden rounded-xl bg-slate-200">
                      <img src={src} alt={t(["v2_sh_before", "v2_sh_framing", "v2_sh_after"][i])} width={500} height={335} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                    <figcaption className="mt-1.5 text-center text-[11px] font-bold text-[#475569]" data-testid={`share-cap-${["v2_sh_before", "v2_sh_framing", "v2_sh_after"][i]}`}>{t(["v2_sh_before", "v2_sh_framing", "v2_sh_after"][i])}</figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-4 text-[13px] leading-relaxed text-[#475569]" data-testid="share-small">{t("v2_sh_small")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Job costing — navy band. Text first on phones, mockup after. */}
      <section style={{ background: NAVY }} data-testid="landing-costing">
        <div className={`${wrap} flex flex-wrap items-center gap-6 py-6 md:gap-10 md:py-20`}>
          <div className="min-w-0 flex-[1_1_420px]">
            <SectionLabel color="#8FC3FF" testid="jc-label">{t("v2_jc_label")}</SectionLabel>
            <h2 className="mt-3 text-[clamp(28px,3.4vw,40px)] font-extrabold tracking-tight text-white" data-testid="jc-h2">{t("v2_jc_h2")}</h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#C7D3E3] md:text-base" data-testid="jc-body">{t("v2_jc_body")}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["v2_jc_pill1", "v2_jc_pill2", "v2_jc_pill3"].map((k) => (
                <span key={k} className="rounded-full px-3.5 py-2 text-[13px] font-bold text-[#C7D3E3]" style={{ background: CHIP_NAVY }} data-testid={`jc-pill-${k}`}>
                  {t(k)}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-[1_1_420px] justify-center">
            <div className="w-full max-w-[340px] rounded-[20px] border border-[#E4EAF2] bg-white p-5 shadow-2xl" data-testid="jc-card">
              <p className="text-[13px] font-extrabold text-[#0F172A]" data-testid="jc-card-project">{t("v2_mock_project")}</p>
              <p className="mt-1 text-[11px] font-extrabold uppercase tracking-widest text-[#475569]">{t("v2_jc_card_title")}</p>
              <p className="mt-1 text-[32px] font-extrabold text-[#0F172A]" data-testid="jc-total">$4,280.50</p>
              <div className="mt-3 space-y-2.5">
                {COST_ROWS.map(([k, amt]) => (
                  <div key={k} className="flex items-center justify-between" data-testid={`jc-row-${k}`}>
                    <span className="text-sm font-semibold text-[#334155]">{t(k)}</span>
                    <span className="text-sm font-extrabold text-[#0F172A]">{amt}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl border-2 border-dashed border-[#E4EAF2] px-3 py-2 md:py-2.5" data-testid="jc-receipt-read">
                <Camera className="h-4 w-4 shrink-0" style={{ color: BLUE }} />
                <span className="text-[12px] font-semibold text-[#475569]">{t("v2_jc_read")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Estimates, change orders and invoices — the money workflow */}
      <section className="bg-white" data-testid="landing-money-docs">
        <div className={`${wrap} flex flex-wrap items-center gap-6 py-6 md:gap-10 md:py-20`}>
          <div className="min-w-0 flex-[1_1_420px]">
            <SectionLabel color="#1259C3" testid="money-label">{t("v2_md_label")}</SectionLabel>
            <h2 className={`mt-3 ${h2cls}`} data-testid="money-h2">{t("v2_md_h2")}</h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#334155] md:text-base" data-testid="money-body">{t("v2_md_body")}</p>
            <ol className="mt-5 space-y-3 md:mt-7">
              {["v2_md_s1", "v2_md_s2", "v2_md_s3"].map((k, i) => (
                <li key={k} className="flex items-start gap-3" data-testid={`money-step-${i + 1}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold text-white" style={{ background: BLUE }}>{i + 1}</span>
                  <div>
                    <p className="text-[15px] font-extrabold text-[#0F172A]">{t(`${k}_t`)}</p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-[#475569]">{t(`${k}_d`)}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-5 flex items-center gap-2 rounded-xl px-4 py-3 text-[13px] font-bold text-[#0F172A]" style={{ background: LIGHT }} data-testid="money-pay-claim">
              <Check className="h-4 w-4 shrink-0" style={{ color: BLUE }} /> {t("v2_md_pay")}
            </p>
          </div>
          <div className="flex-[1_1_420px]">
            <div className="rounded-[20px] border border-[#E4EAF2] p-5 md:p-6" style={{ background: LIGHT }} data-testid="money-card">
              <div className="grid grid-cols-3 gap-3">
                {[["/landing/v2/story-2.webp", "v2_md_cap_before"], ["/landing/v2/story-4.webp", "v2_md_cap_progress"], ["/landing/v2/story-6.webp", "v2_md_cap_after"]].map(([src, k]) => (
                  <figure key={k}>
                    <div className="aspect-[4/3] overflow-hidden rounded-xl bg-slate-200">
                      <img src={src} alt={t(k)} width={500} height={335} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                    <figcaption className="mt-1.5 text-center text-[11px] font-bold text-[#475569]" data-testid={`money-cap-${k}`}>{t(k)}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="mt-4 rounded-xl border border-[#E4EAF2] bg-white p-4" data-testid="money-mock">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-extrabold text-[#0F172A]" data-testid="money-mock-no">{t("v2_md_mock_no")}</span>
                  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-extrabold uppercase text-green-700" data-testid="money-mock-status">{t("v2_md_mock_status")}</span>
                </div>
                <div className="mt-3 space-y-1.5 text-[13px]">
                  <div className="flex justify-between"><span className="font-semibold text-[#475569]">{t("v2_md_mock_contract")}</span><span className="font-extrabold text-[#0F172A]">$13,970.00</span></div>
                  <div className="flex justify-between"><span className="font-semibold text-[#475569]">{t("v2_md_mock_invoiced")}</span><span className="font-extrabold text-[#0F172A]">$3,756.00</span></div>
                  <div className="flex justify-between"><span className="font-semibold text-[#475569]">{t("v2_md_mock_left")}</span><span className="font-extrabold text-[#0F172A]">$10,214.00</span></div>
                </div>
              </div>
              <p className="mt-3 text-[12px] font-semibold text-[#475569]" data-testid="money-mock-note">{t("v2_md_mock_note")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Schedule + 360 Assistant — two equal cards, stack on phones */}
      <section className="bg-white" data-testid="landing-sched-360">
        <div className={`${wrap} flex flex-wrap gap-5 py-6 md:gap-6 md:py-20`}>
          <div className="min-w-0 flex-[1_1_420px] rounded-[20px] border border-[#E4EAF2] p-5 md:p-7" data-testid="sched-card">
            <SectionLabel color="#1259C3" testid="sched-label">{t("v2_sc_label")}</SectionLabel>
            <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-[#0F172A]" data-testid="sched-h3">{t("v2_sc_h3")}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#334155] md:text-base" data-testid="sched-body">{t("v2_sc_body")}</p>
            <div className="mt-5 space-y-2.5">
              {["v2_sc_row1", "v2_sc_row2"].map((k) => (
                <div key={k} className="flex items-center gap-3 rounded-xl px-4 py-2.5 md:py-3" style={{ background: LIGHT }} data-testid={`sched-row-${k}`}>
                  <Check className="h-4 w-4 shrink-0" style={{ color: BLUE }} />
                  <span className="text-sm font-semibold text-[#0F172A]">{t(k)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="min-w-0 flex-[1_1_420px] rounded-[20px] border border-[#E4EAF2] p-5 md:p-7" data-testid="a360-card">
            <SectionLabel color="#1259C3" testid="a360-label">{t("v2_a_label")}</SectionLabel>
            <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-[#0F172A]" data-testid="a360-h3">{t("v2_a_h3")}</h3>
            <p className="mt-4 rounded-xl px-4 py-3.5 text-[15px] italic leading-relaxed text-[#334155]" style={{ background: LIGHT }} data-testid="a360-quote">
              “{t("v2_a_quote")}”
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["v2_a_chip1", "v2_a_chip2", "v2_a_chip3"].map((k) => (
                <span key={k} className="rounded-full border border-[#E4EAF2] px-3.5 py-1.5 text-[13px] font-bold text-[#0F172A]" data-testid={`a360-chip-${k}`}>{t(k)}</span>
              ))}
            </div>
            <p className="mt-4 text-[13px] font-semibold text-[#475569]" data-testid="a360-small">{t("v2_a_small")}</p>
          </div>
        </div>
      </section>

      {/* 7. Pricing — light, one card */}
      <section id="pricing" style={{ background: LIGHT }} data-testid="landing-pricing">
        <div className={`${wrap} py-6 md:py-20`}>
          <h2 className={`text-center ${h2cls}`} data-testid="pricing-h2">{t("v2_pr_h2")}</h2>
          {/* Monthly / Yearly — yearly default (save 2 months) */}
          <div className="mt-5 flex justify-center" data-testid="pricing-toggle">
            <div className="flex items-center gap-1 rounded-full border border-[#E4EAF2] bg-white p-1">
              <button onClick={() => setBillInt("month")} data-testid="pricing-toggle-monthly"
                className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${billInt === "month" ? "bg-[#1565D8] text-white" : "text-[#475569]"}`}>
                {t("billing_monthly")}
              </button>
              <button onClick={() => setBillInt("year")} data-testid="pricing-toggle-yearly"
                className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${billInt === "year" ? "bg-[#1565D8] text-white" : "text-[#475569]"}`}>
                {t("billing_annual")} · {t("billing_save_two_months")}
              </button>
            </div>
          </div>
          <div className="mx-auto mt-6 grid max-w-[860px] grid-cols-1 gap-4 md:mt-8 md:grid-cols-2 md:gap-6">
            {[
              { key: "ess", name: t("billing_plan_essentials"), m: 59, y: 49, seats: 3, popular: false },
              { key: "pro", name: t("billing_plan_pro"), m: 129, y: 109, seats: 5, popular: true },
            ].map((p) => (
              <div key={p.key} className={`relative rounded-[20px] border-2 bg-white p-5 md:p-7 ${p.popular ? "border-[#1565D8]" : "border-[#E4EAF2]"}`} data-testid={`price-card-${p.key}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#1565D8] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white" data-testid="price-popular">
                    {t("billing_most_popular")}
                  </span>
                )}
                <div className="text-center">
                  <div className={`${label} text-[#475569]`}>{p.name}</div>
                  <span className="text-5xl font-extrabold tracking-tight text-[#0F172A]" data-testid={`landing-price-${p.key}`}>${billInt === "year" ? p.y : p.m}</span>
                  <span className="text-base font-semibold text-[#475569]">{billInt === "year" ? t("billing_per_month_yearly") : t("v2_pr_per_month")}</span>
                  <p className="mt-2 text-sm font-bold text-[#0F172A]" data-testid={`price-seats-${p.key}`}>{t("billing_seats_line", { n: p.seats })}</p>
                </div>
                <ul className="mt-5 space-y-2.5 md:mt-6 md:space-y-3">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <li key={n} className="flex items-start gap-2.5" data-testid={`price-${p.key}-f${n}`}>
                      <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BLUE }} />
                      <span className="text-sm font-semibold text-[#0F172A]">{t(`plan_${p.key}_f${n}`)}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={toSignup} data-testid={`cta-start-free-${p.key}`} className={`mt-7 w-full ${ctaMain}`}>
                  {t("v2_cta")} <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-[13px] font-semibold text-[#475569]" data-testid="price-under">{t("v2_pr_trial")}</p>
          <p className="mt-2 text-center text-[13px] font-bold text-[#166534]" data-testid="price-guarantee">{t("v2_pr_guarantee")}</p>
          {founding?.active && (
            <p className="mt-2 text-center text-[13px] font-extrabold text-[#B45309]" data-testid="price-founding">
              {t("v2_pr_founding", { n: founding.left })}
            </p>
          )}
          {/* Compare all features */}
          <div className="mx-auto mt-8 max-w-[640px] overflow-hidden rounded-[20px] border border-[#E4EAF2] bg-white" data-testid="price-compare">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4EAF2] text-left">
                  <th className="px-4 py-3 font-bold text-[#475569]">{t("v2_cmp_feature")}</th>
                  <th className="px-4 py-3 text-center font-bold text-[#0F172A]">{t("billing_plan_essentials")}</th>
                  <th className="px-4 py-3 text-center font-bold text-[#1565D8]">{t("billing_plan_pro")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["v2_cmp_users", "3", "5"],
                  ["v2_cmp_photos", 1, 1],
                  ["v2_cmp_sched", 1, 1],
                  ["v2_cmp_money", 1, 1],
                  ["v2_cmp_gantt", 0, 1],
                  ["v2_cmp_moneypro", 0, 1],
                  ["v2_cmp_web", 0, 1],
                  ["v2_cmp_support", 0, 1],
                ].map(([k, ess, pro]) => (
                  <tr key={k} className="border-b border-[#F0F4FA] last:border-0" data-testid={`cmp-${k}`}>
                    <td className="px-4 py-2.5 font-semibold text-[#0F172A]">{t(k)}</td>
                    <td className="px-4 py-2.5 text-center">{ess === 1 ? <Check className="mx-auto h-4 w-4 text-[#1565D8]" /> : ess === 0 ? <X className="mx-auto h-4 w-4 text-[#CBD5E1]" /> : <span className="font-bold">{ess}</span>}</td>
                    <td className="px-4 py-2.5 text-center">{pro === 1 ? <Check className="mx-auto h-4 w-4 text-[#1565D8]" /> : <span className="font-bold">{pro}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mx-auto mt-5 max-w-[560px] text-center text-[12px] font-semibold text-[#475569]" data-testid="price-vs">{t("v2_pr_vs")}</p>
        </div>
      </section>

      {/* 8. FAQ — exactly 5 questions, current answers */}
      <section id="faq" className="bg-white" data-testid="landing-faq">
        <div className="mx-auto w-full max-w-[760px] px-5 py-6 md:py-20">
          <h2 className={`text-center ${h2cls}`} data-testid="faq-h2">{t("v2_faq_h2")}</h2>
          <div className="mt-6 space-y-2.5 md:mt-8 md:space-y-3">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <details key={n} className="rounded-[20px] border border-[#E4EAF2] px-5 py-3 md:py-4" style={{ background: LIGHT }} data-testid={`land-faq-${n}`}>
                <summary className="min-h-[28px] cursor-pointer text-sm font-bold text-[#0F172A]">{t(`v2_faq_q${n}`)}</summary>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{t(`v2_faq_a${n}`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Final CTA — navy, centered */}
      <section style={{ background: NAVY }} data-testid="landing-final-cta">
        <div className={`${wrap} py-7 text-center md:py-20`}>
          <h2 className="text-[clamp(28px,3.4vw,40px)] font-extrabold tracking-tight text-white" data-testid="final-h2">{t("v2_fi_h2")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-[#C7D3E3]" data-testid="final-body">{t("v2_fi_body")}</p>
          <button onClick={toSignup} data-testid="cta-start-free-final" className={`mt-7 ${ctaMain}`}>
            {t("v2_cta")} <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-3.5 text-sm font-semibold text-[#A9B8CC]" data-testid="final-small">{t("v2_fi_small")}</p>
        </div>
      </section>

      {/* 10. Footer */}
      <footer style={{ background: FOOTER_BG }} data-testid="landing-footer">
        <div className={`${wrap} flex flex-col items-center justify-between gap-3 py-4 sm:flex-row sm:py-6`}>
          <span className="text-xs text-[#A9B8CC]" data-testid="footer-copy">{t("v2_fo_rights")}</span>
          <span className="text-[10px] text-slate-500" data-testid="footer-ftc">
            {t("footer_ftc", "Affiliate disclosure: BuildProject360 partners may earn a commission when you sign up through their link, at no extra cost to you.")}
          </span>
          <span className="text-[10px] text-slate-500" data-testid="footer-address">
            {t("footer_address", "BuildProject360 LLC, 2722 Erie Ave Ste 219 PMB 919133, Cincinnati, OH 45208")} ·{" "}
            <a href="mailto:admin@buildproject360.com" className="hover:text-white" data-testid="footer-email">admin@buildproject360.com</a>
          </span>
          <Link to="/partner-signup" data-testid="footer-partner" className="inline-flex min-h-[44px] items-center text-xs font-semibold transition-colors hover:text-white">{t("footer_partner", "Become a partner")}</Link>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs font-semibold text-[#C7D3E3]">
            <Link to="/terms" data-testid="footer-terms" className="inline-flex min-h-[44px] items-center transition-colors hover:text-white">{t("footer_terms")}</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="inline-flex min-h-[44px] items-center transition-colors hover:text-white">{t("footer_privacy")}</Link>
            <button onClick={() => changeLang(isEs ? "en" : "es")} data-testid="footer-lang"
              className="inline-flex min-h-[44px] items-center transition-colors hover:text-white">
              {isEs ? "English" : "Español"}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
